/* Auto-apply coupon (DG-2431). */
(function () {
  'use strict';

  const SESSION_KEY = 'fastrrCouponCodeFromLink';
  // Internal binding — `fastrrCouponCodeFromLink` is a 3rd-party contract and
  // stays a plain code string; this side-channel records which product *we*
  // saved that code for, so cross-product navigation doesn't auto-apply.
  const PRODUCT_LOCK_KEY = 'fastrrCouponProductLock';
  const URL_PARAM = 'fastrrCouponCodeFromLink';
  const CONFETTI_SRC = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
  const POPUP_AUTO_CLOSE_MS = 3000;
  // motionone build doesn't ship spring(); cubic-bezier overshoot mimics the feel.
  const SPRING_EASING = [0.34, 1.56, 0.64, 1];

  const API_URL = 'https://sr-channel.shiprocket.in/v1/zop/tracker/deal-data';
  const API_KEY = 'qDIbkxy9bUFCJ8ATi5YsvkYZaeGZ9i4X';

  const state = {
    productId: null,
    productHandle: null,
    sectionId: null,
    payload: null,
    shouldCelebrate: false,
    listenerBound: false,
    fetchedVariants: null,
  };

  let inFlight = null;
  let productFetchPromise = null;

  // API returns rupees; internal pipeline works in paise — multiply by 100.
  async function validateCoupon({ variantId, signal }) {
    if (!variantId) return null;
    // await new Promise((r) => setTimeout(r, 00));
    let json;
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + API_KEY,
        },
        body: JSON.stringify({ variant_id: String(variantId) }),
        signal,
      });
      if (!response.ok) return null;
      json = await response.json();
    } catch (e) {
      if (e && e.name === 'AbortError') throw e;
      return null;
    }
    if (!json || !json.data || json.data.price == null) return null;
    return { final_price: Number(json.data.price) * 100 };
  }

  function readUrlCoupon() {
    try {
      const raw = new URLSearchParams(window.location.search).get(URL_PARAM);
      const trimmed = (raw || '').trim();
      return trimmed || null;
    } catch (e) {
      return null;
    }
  }

  function readSessionCoupon() {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      const code = (raw || '').trim();
      if (!code) return null;

      let lock = null;
      try { lock = JSON.parse(sessionStorage.getItem(PRODUCT_LOCK_KEY) || 'null'); } catch (e) {}

      // Lock is authoritative only while its code still matches what's in
      // SESSION_KEY — if a 3rd party overwrote the code, drop the binding and
      // honor their value as-is.
      if (lock && lock.code === code) {
        if (String(lock.productId) !== String(state.productId)) return null;
      }
      return code;
    } catch (e) {
      return null;
    }
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) cents = 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format || '₹{{amount_no_decimals}}');
      } catch (e) {}
    }
    const rupees = Math.round(cents / 100);
    return '₹' + rupees.toLocaleString('en-IN');
  }

  function buildPayload(couponCode, variant, validationResult) {
    const sellingPrice = Number(variant.price) || 0;
    const listingPrice = Number(variant.compare_at_price) > sellingPrice
      ? Number(variant.compare_at_price)
      : sellingPrice;
    const discountedPrice = Number(validationResult.final_price) || sellingPrice;
    const savings = sellingPrice - discountedPrice;
    const savingsTotal = listingPrice - discountedPrice;
    const discountPercent = listingPrice > 0
      ? Math.round((savingsTotal / listingPrice) * 100)
      : 0;
    return {
      couponCode,
      listingPrice,
      sellingPrice,
      discountedPrice,
      savings: Math.max(0, savings),
      savingsTotal: Math.max(0, savingsTotal),
      discountPercent: Math.max(0, discountPercent),
      variantId: variant.id,
    };
  }

  function naturalDiscountPercent(variant) {
    const sellingPrice = Number(variant.price) || 0;
    const compareAt = Number(variant.compare_at_price) || 0;
    if (compareAt <= sellingPrice) return 0;
    return Math.round(((compareAt - sellingPrice) / compareAt) * 100);
  }

  function getEmbeddedVariants() {
    const json = document.querySelector('variant-selects script[type="application/json"]');
    if (!json) return null;
    try { return JSON.parse(json.textContent); } catch (e) { return null; }
  }

  // Single-variant products don't render <variant-selects>, so the embedded
  // JSON is absent. Fall back to Shopify's product JSON endpoint, which
  // returns prices in paise (same units as the embedded JSON).
  function fetchProductVariants() {
    if (state.fetchedVariants) return Promise.resolve(state.fetchedVariants);
    if (productFetchPromise) return productFetchPromise;
    if (!state.productHandle) return Promise.resolve(null);

    productFetchPromise = fetch('/products/' + encodeURIComponent(state.productHandle) + '.js', {
      headers: { 'Accept': 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const variants = data && Array.isArray(data.variants) ? data.variants : null;
        state.fetchedVariants = variants;
        return variants;
      })
      .catch(() => null)
      .finally(() => { productFetchPromise = null; });

    return productFetchPromise;
  }

  function pickVariant(variants, targetId) {
    if (!variants || !variants.length) return null;
    return (targetId && variants.find((v) => v.id === targetId))
      || variants.find((v) => v.available)
      || variants[0]
      || null;
  }

  async function getCurrentVariant() {
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects && variantSelects.currentVariant) return variantSelects.currentVariant;

    const idInput = document.querySelector('input[name="id"]');
    const targetId = idInput && Number(idInput.value);

    const embedded = getEmbeddedVariants();
    if (embedded) return pickVariant(embedded, targetId);

    const fetched = await fetchProductVariants();
    return pickVariant(fetched, targetId);
  }

  function motionReduced() {
    return !!(window.FoxTheme && window.FoxTheme.config && window.FoxTheme.config.motionReduced);
  }

  function hasMotion() {
    return !!(window.FoxTheme && window.FoxTheme.Motion && window.FoxTheme.Motion.animate);
  }

  function safeAnimate(target, props, opts) {
    if (motionReduced() || !hasMotion()) return null;
    try {
      return window.FoxTheme.Motion.animate(target, props, opts || {});
    } catch (e) {
      return null;
    }
  }

  // Falls back to rAF when motionone isn't loaded yet.
  function tweenValue(from, to, duration, easing, onUpdate) {
    if (motionReduced()) {
      onUpdate(to);
      return Promise.resolve();
    }
    const dur = duration || 0.6;
    if (hasMotion()) {
      try {
        const controls = window.FoxTheme.Motion.animate(
          (progress) => onUpdate(from + (to - from) * progress),
          { duration: dur, easing: easing || 'ease-out' }
        );
        // Motion's value callback can stop a frame short of progress 1 (and an
        // interrupted animation never reaches it at all), leaving the count-up
        // stranded just shy of the target — this is what left the popup final
        // price frozen at ₹221 instead of ₹199. Snap to the exact terminal
        // value once the animation settles, whether it finished or was cut off.
        if (controls && controls.finished) {
          const snap = () => onUpdate(to);
          return controls.finished.then(snap, snap);
        }
      } catch (e) { /* fall through to rAF */ }
    }
    return new Promise((resolve) => {
      const start = performance.now();
      const ms = dur * 1000;
      function step(now) {
        const t = Math.min(1, (now - start) / ms);
        const eased = 1 - Math.pow(1 - t, 4); // easeOutQuart
        onUpdate(from + (to - from) * eased);
        if (t < 1) requestAnimationFrame(step);
        else resolve();
      }
      requestAnimationFrame(step);
    });
  }

  function tweenPriceText(el, fromCents, toCents, duration) {
    return tweenValue(fromCents, toCents, duration || 0.8, [0.16, 1, 0.3, 1], (value) => {
      el.textContent = formatMoney(value);
    });
  }

  function tweenStrikeProgress(el, duration, delay) {
    if (motionReduced()) {
      el.style.setProperty('--strike-progress', '1');
      return Promise.resolve();
    }
    return safeAnimate(
      el,
      { '--strike-progress': [0, 1] },
      { duration: duration || 0.5, delay: delay || 0, easing: [0.16, 1, 0.3, 1] }
    )?.finished || Promise.resolve();
  }

  let confettiPromise = null;
  function loadConfetti() {
    if (window.confetti) return Promise.resolve(window.confetti);
    if (confettiPromise) return confettiPromise;
    confettiPromise = new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = CONFETTI_SRC;
      s.async = true;
      s.onload = () => resolve(window.confetti || null);
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    });
    return confettiPromise;
  }

  function fireConfetti(stillOpen) {
    return loadConfetti().then((confettiFn) => {
      if (!confettiFn) return;
      if (typeof stillOpen === 'function' && !stillOpen()) return;
      confettiFn({
        particleCount: 130,
        spread: 100,
        origin: { y: 0.4 },
        scalar: 0.9,
        ticks: 300,
        zIndex: 70,
      });
    });
  }

  function getDesktopPriceContainer() {
    if (!state.sectionId) return null;
    return document.getElementById('price-' + state.sectionId);
  }

  // variant-selects.js dispatches 'variant:changed' synchronously, but its
  // section reload that overwrites #price-{section}.innerHTML happens later
  // when the fetch resolves. The theme publishes PUB_SUB_EVENTS.variantChange
  // AFTER that overwrite, so subscribing there gives us a clean "DOM is fresh"
  // signal — no race, no MutationObserver needed.
  function waitForVariantChange(timeoutMs) {
    return new Promise((resolve) => {
      const pubsub = window.FoxTheme.pubsub;
      let done = false;
      const finish = () => { if (done) return; done = true; unsub(); resolve(); };
      const unsub = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, finish);
      setTimeout(finish, timeoutMs);
    });
  }

  function animateDesktopPrice(payload) {
    const container = getDesktopPriceContainer();
    if (!container) return;
    const fPrice = container.querySelector('.f-price');
    if (fPrice) fPrice.classList.add('f-price--on-sale');

    const saleEl = container.querySelector('.f-price__sale .f-price-item--sale.priceRHS');
    const compareEl = container.querySelector('.f-price__sale .comparePriceRHS s');
    const badgeEl = container.querySelector('.zop-discount-badge');
    const badgeWrap = badgeEl && badgeEl.closest('[data-type]');

    if (compareEl) compareEl.textContent = formatMoney(payload.listingPrice);
    if (badgeEl) {
      badgeEl.textContent = payload.discountPercent + '% Off';
      if (badgeWrap) badgeWrap.style.display = '';
    }

    if (saleEl) tweenPriceText(saleEl, payload.sellingPrice, payload.discountedPrice, 0.8);
    if (compareEl) {
      safeAnimate(
        compareEl,
        { opacity: [0, 1], transform: ['translateY(-4px)', 'translateY(0)'] },
        { duration: 0.25, delay: 0.2 }
      );
    }
    if (badgeEl) {
      safeAnimate(
        badgeEl,
        { transform: ['scale(0.6)', 'scale(1.1)', 'scale(1)'] },
        { duration: 0.4, delay: 0.3 }
      );
    }
  }

  function revertDesktopPrice(variant) {
    const container = getDesktopPriceContainer();
    if (!container) return;
    const fPrice = container.querySelector('.f-price');
    const saleEl = container.querySelector('.f-price__sale .f-price-item--sale.priceRHS');
    const compareEl = container.querySelector('.f-price__sale .comparePriceRHS s');
    const badgeEl = container.querySelector('.zop-discount-badge');
    const badgeWrap = badgeEl && badgeEl.closest('[data-type]');
    const savingsEl = container.querySelector('.zop-coupon-savings');

    const sellingPrice = Number(variant.price) || 0;
    const compareAt = Number(variant.compare_at_price) || 0;
    const naturallyOnSale = compareAt > sellingPrice;
    const pct = naturalDiscountPercent(variant);

    if (saleEl) saleEl.textContent = formatMoney(sellingPrice);
    if (compareEl) compareEl.textContent = naturallyOnSale ? formatMoney(compareAt) : '';
    if (badgeEl) {
      if (naturallyOnSale) {
        badgeEl.textContent = pct + '% Off';
        if (badgeWrap) badgeWrap.style.display = '';
      } else {
        badgeEl.textContent = '';
        if (badgeWrap) badgeWrap.style.display = 'none';
      }
    }
    if (fPrice) fPrice.classList.toggle('f-price--on-sale', naturallyOnSale);
    if (savingsEl) savingsEl.remove();
  }

  // Sticky liquid only renders compare price / discount badge when the product
  // is naturally on sale. Inject equivalents for coupon-only discounts so the
  // existing CSS picks them up; data-zop-injected lets revert clean them up.
  function ensureStickyCompareEl(container) {
    let el = container.querySelector('.comparePrice-s');
    if (el) return el;
    const host = container.querySelector('.f-price--sticky') || container.querySelector('.f-price');
    if (!host) return null;
    el = document.createElement('div');
    el.className = 'f-price-item f-price-item--regular comparePrice-s';
    el.setAttribute('data-zop-injected', '');
    host.appendChild(el);
    return el;
  }

  function ensureStickyBadgeEl(container) {
    let el = container.querySelector('.zop-discount-badge');
    if (el) return el;
    const host = container.querySelector('.f-price--sticky') || container.querySelector('.f-price');
    if (!host) return null;
    el = document.createElement('div');
    el.className = 'zop-discount-badge discount-badge-sticky badge-grad-1';
    el.setAttribute('data-zop-injected', '');
    host.appendChild(el);
    return el;
  }

  function animateStickyPrice(payload) {
    const container = document.querySelector('.price-sticky-container');
    if (!container) return;

    const fPrice = container.querySelector('.f-price');
    if (fPrice) fPrice.classList.add('f-price--on-sale');

    const priceEl   = container.querySelector('.priceText');
    const compareEl = ensureStickyCompareEl(container);
    const badgeEl   = ensureStickyBadgeEl(container);

    if (compareEl) {
      compareEl.innerHTML = '<s>' + formatMoney(payload.listingPrice) + '</s>';
      compareEl.style.display = 'inline';
    }
    if (badgeEl) {
      badgeEl.textContent = payload.discountPercent + '% Off';
      badgeEl.style.display = '';
    }
    if (priceEl) tweenPriceText(priceEl, payload.sellingPrice, payload.discountedPrice, 0.8);

    const stickyBadge = container.querySelector('.zop-sticky-badge');
    if (stickyBadge) stickyBadge.style.display = 'none';

    let savingsEl = container.querySelector('[data-sticky-coupon-savings]');
    if (!savingsEl) {
      savingsEl = document.createElement('div');
      savingsEl.setAttribute('data-sticky-coupon-savings', '');
      savingsEl.className = 'zop-sticky-coupon-savings';
      container.appendChild(savingsEl);
    }
    savingsEl.textContent = '✓ You save ' + formatMoney(payload.savingsTotal);
    savingsEl.style.display = '';
  }

  function revertStickyPrice(variant) {
    const container = document.querySelector('.price-sticky-container');
    if (!container) return;

    const fPrice = container.querySelector('.f-price');
    const priceEl = container.querySelector('.priceText');
    const compareEl = container.querySelector('.comparePrice-s');
    const badgeEl = container.querySelector('.zop-discount-badge');
    const stickyBadge = container.querySelector('.zop-sticky-badge');
    const savingsEl = container.querySelector('[data-sticky-coupon-savings]');

    const sellingPrice = Number(variant.price) || 0;
    const compareAt = Number(variant.compare_at_price) || 0;
    const naturallyOnSale = compareAt > sellingPrice;
    const pct = naturalDiscountPercent(variant);

    if (priceEl) priceEl.textContent = formatMoney(sellingPrice);
    if (compareEl) {
      if (compareEl.hasAttribute('data-zop-injected')) {
        compareEl.remove();
      } else if (naturallyOnSale) {
        compareEl.innerHTML = '<s>' + formatMoney(compareAt) + '</s>';
        compareEl.style.display = 'inline';
      } else {
        compareEl.innerHTML = '';
        compareEl.style.display = 'none';
      }
    }
    if (badgeEl) {
      if (badgeEl.hasAttribute('data-zop-injected')) {
        badgeEl.remove();
      } else if (naturallyOnSale) {
        badgeEl.textContent = pct + '% Off';
        badgeEl.style.display = '';
      } else {
        badgeEl.textContent = '';
        badgeEl.style.display = 'none';
      }
    }
    if (fPrice) fPrice.classList.toggle('f-price--on-sale', naturallyOnSale);
    if (stickyBadge) stickyBadge.style.display = '';
    if (savingsEl) savingsEl.remove();
  }

  function paintPromoSlot(payload) {
    const slot = document.querySelector('[data-promo-slot]');
    if (!slot) return null;
    slot.dataset.couponApplied = '1';
    const appliedCard = slot.querySelector('[data-applied-coupon-card]');
    if (!appliedCard) return null;

    const codeEl = appliedCard.querySelector('[data-coupon-code]');
    const savingsEl = appliedCard.querySelector('[data-coupon-savings]');
    const copyBtn = appliedCard.querySelector('[data-coupon-copy]');
    if (codeEl) codeEl.textContent = payload.couponCode;
    if (savingsEl) savingsEl.textContent = formatMoney(payload.savingsTotal);
    if (copyBtn) {
      copyBtn.onclick = () => {
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(payload.couponCode).catch(() => {});
        copyBtn.setAttribute('data-copied', '');
        setTimeout(() => copyBtn.removeAttribute('data-copied'), 1500);
      };
    }

    appliedCard.removeAttribute('hidden');
    return appliedCard;
  }

  function revertPromoSlot() {
    const slot = document.querySelector('[data-promo-slot]');
    if (!slot) return;
    slot.dataset.couponApplied = '0';
    const appliedCard = slot.querySelector('[data-applied-coupon-card]');
    if (appliedCard) appliedCard.setAttribute('hidden', '');
  }

  function animatePromoSlotEntry(card) {
    if (!card || motionReduced()) return;
    safeAnimate(
      card,
      { transform: ['scale(0.92)', 'scale(1.02)', 'scale(1)'], opacity: [0, 1] },
      { duration: 0.45, easing: SPRING_EASING }
    );
  }

  function applyCouponUI(payload) {
    animateDesktopPrice(payload);
    animateStickyPrice(payload);
    const card = paintPromoSlot(payload);
    if (card) animatePromoSlotEntry(card);
  }

  function revertCouponUI(variant) {
    revertDesktopPrice(variant);
    revertStickyPrice(variant);
    revertPromoSlot();
  }

  function showCelebrationModal(payload) {
    const modal = document.getElementById('ZopCouponAutoApplyPopup');
    if (!modal || typeof modal.show !== 'function') return;

    const finalEl = modal.querySelector('[data-popup-final]');
    const strikeEl = modal.querySelector('.zop-coupon-popup__strike');
    const strikeText = modal.querySelector('[data-popup-strike]');
    const savedEl = modal.querySelector('[data-popup-saved]');

    if (strikeText) strikeText.textContent = formatMoney(payload.sellingPrice);
    if (savedEl) savedEl.textContent = formatMoney(payload.savings);

    if (motionReduced()) {
      if (finalEl) finalEl.textContent = formatMoney(payload.discountedPrice);
      if (strikeEl) strikeEl.style.setProperty('--strike-progress', '1');
      modal.show();
      setTimeout(() => modal.hide(), POPUP_AUTO_CLOSE_MS);
      return;
    }

    if (finalEl) finalEl.textContent = formatMoney(payload.sellingPrice);
    if (strikeEl) strikeEl.style.setProperty('--strike-progress', '0');

    modal.show();

    // Wait for the modal's [active] scale-in so tweens don't race against it.
    modal.addEventListener('modal:handleAfterShow', () => {
      if (finalEl) tweenPriceText(finalEl, payload.sellingPrice, payload.discountedPrice, 0.8);
      if (strikeEl) tweenStrikeProgress(strikeEl, 0.5, 0.1);
      setTimeout(() => fireConfetti(() => modal.open), 300);
      setTimeout(() => { if (modal.open) modal.hide(); }, POPUP_AUTO_CLOSE_MS);
    }, { once: true });
  }

  function saveSession(code) {
    try {
      sessionStorage.setItem(SESSION_KEY, code);
      sessionStorage.setItem(PRODUCT_LOCK_KEY, JSON.stringify({
        code,
        productId: String(state.productId),
      }));
    } catch (e) {}
  }

  // Apply the discount to Shopify's cart so it carries through to checkout.
  function updateCartDiscount(couponCode) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: couponCode }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }

  async function refresh({ awaitDomReload = false } = {}) {
    if (inFlight) inFlight.abort();
    const controller = new AbortController();
    inFlight = controller;

    const variant = await getCurrentVariant();
    if (!variant) return;

    // On variant change, the theme's section reload is in flight; run it in
    // parallel with the API so paint lands once both have settled.
    const reloadWait = awaitDomReload ? waitForVariantChange(1500) : Promise.resolve();

    // URL param wins so a fresh coupon link overrides any sticky session value.
    const couponCode = readUrlCoupon() || readSessionCoupon();
    if (!couponCode) {
      await reloadWait;
      if (inFlight !== controller) return;
      revertCouponUI(variant);
      return;
    }

    let result;
    try {
      [result] = await Promise.all([
        validateCoupon({ variantId: variant.id, signal: controller.signal }),
        reloadWait,
      ]);
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      revertCouponUI(variant);
      return;
    }

    // A newer refresh has overtaken this one — drop the stale result.
    if (inFlight !== controller) return;

    if (!result || result.final_price == null) {
      revertCouponUI(variant);
      return;
    }

    const payload = buildPayload(couponCode, variant, result);
    state.payload = payload;

    applyCouponUI(payload);
    saveSession(payload.couponCode);

    if (state.shouldCelebrate) {
      state.shouldCelebrate = false;
      showCelebrationModal(payload);
      updateCartDiscount(payload.couponCode);
    }
  }

  function init({ productId, productHandle, sectionId }) {
    if (!productId || !sectionId) return;
    state.productId = productId;
    state.productHandle = productHandle || null;
    state.sectionId = sectionId;

    // Celebrate once per "new coupon arrival": URL param differs from what
    // session already saw. Compared BEFORE the first refresh writes session,
    // so a repeat visit with the same code stays silent.
    const urlCode = readUrlCoupon();
    const sessionCode = readSessionCoupon();
    if (urlCode && urlCode !== sessionCode) {
      state.shouldCelebrate = true;
      // Pre-warm confetti so the burst lands without a CDN delay when the modal fires.
      loadConfetti();
    }

    if (!state.listenerBound) {
      state.listenerBound = true;
      document.addEventListener('variant:changed', () => refresh({ awaitDomReload: true }));
    }

    refresh();
  }

  window.ZopCouponAutoApply = { init };

  // Deferred scripts execute after DOM parse, so document.currentScript is null
  // by the time this IIFE runs — querySelector is the path to the bootstrap tag.
  const bootstrapTag = document.querySelector('script[data-zop-coupon-bootstrap]');
  if (bootstrapTag && bootstrapTag.dataset.productId && bootstrapTag.dataset.sectionId) {
    init({
      productId: bootstrapTag.dataset.productId,
      sectionId: bootstrapTag.dataset.sectionId,
      productHandle: bootstrapTag.dataset.productHandle || '',
    });
  }
})();
